<template>
  <div class="broadcast-layout">
    <Sidebar />

    <div class="main-content">
      <TopHeader />

      <main class="page-content">
        <div class="recipients-container">

          <!-- Back + Page Header -->
          <div class="page-header">
            <button @click="$router.push('/whatsapp-broadcast')" class="back-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
              Back to Campaigns
            </button>

            <div v-if="campaign" class="campaign-header-info">
              <div class="campaign-title-row">
                <h1>{{ campaign.campaign_name }}</h1>
                <span :class="['status-badge', campaign.status]">{{ campaign.status.replace('_', ' ') }}</span>
              </div>
              <p class="campaign-meta">
                {{ campaign.total_recipients.toLocaleString() }} total recipients
                &nbsp;·&nbsp;
                Created {{ formatDate(campaign.created_at) }}
              </p>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading && !campaign" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading campaign data...</p>
          </div>

          <template v-else-if="campaign">
            <!-- Summary Stat Cards -->
            <div class="summary-grid">
              <div class="summary-card sent">
                <div class="summary-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                </div>
                <div class="summary-body">
                  <div class="summary-value">{{ campaign.sent_count.toLocaleString() }}</div>
                  <div class="summary-label">Sent</div>
                  <div class="summary-pct">{{ pct(campaign.sent_count) }}% of total</div>
                </div>
              </div>

              <div class="summary-card delivered">
                <div class="summary-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 7l-1.41-1.41-6.34 6.34 1.41 1.41L18 7zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41zM.41 13.41L6 19l1.41-1.41L1.83 12 .41 13.41z"/>
                  </svg>
                </div>
                <div class="summary-body">
                  <div class="summary-value">{{ campaign.delivered_count.toLocaleString() }}</div>
                  <div class="summary-label">Delivered</div>
                  <div class="summary-pct">{{ pct(campaign.delivered_count) }}% of total</div>
                </div>
              </div>

              <div class="summary-card read">
                <div class="summary-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                </div>
                <div class="summary-body">
                  <div class="summary-value">{{ campaign.read_count.toLocaleString() }}</div>
                  <div class="summary-label">Read</div>
                  <div class="summary-pct">{{ pct(campaign.read_count) }}% of total</div>
                </div>
              </div>

              <div class="summary-card failed">
                <div class="summary-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                </div>
                <div class="summary-body">
                  <div class="summary-value">{{ campaign.failed_count.toLocaleString() }}</div>
                  <div class="summary-label">Failed</div>
                  <div class="summary-pct">{{ pct(campaign.failed_count) }}% of total</div>
                </div>
              </div>
            </div>

            <!-- Read Rate Timeline -->
            <div v-if="timeline.length > 0" class="timeline-section">
              <div class="timeline-header">
                <div class="timeline-title-group">
                  <h3>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="color:#25D366;margin-right:6px;vertical-align:-2px">
                      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
                    </svg>
                    When Did Recipients Read This?
                  </h3>
                  <p class="timeline-subtitle">
                    Each bar shows how many people opened the message during that hour.
                    Use this to pick the best send time for your next campaign.
                  </p>
                </div>
                <div class="timeline-peak-badge" v-if="timelinePeak">
                  <span class="peak-label">Peak hour</span>
                  <span class="peak-value">{{ timelinePeak.label }}</span>
                  <span class="peak-count">{{ timelinePeak.count }} reads</span>
                </div>
              </div>
              <div class="timeline-chart">
                <div
                  v-for="bucket in timeline"
                  :key="bucket.hour_bucket"
                  class="timeline-bar-wrap"
                  :class="{ 'is-peak': parseInt(bucket.count) === timelineMax }"
                >
                  <span class="bar-count">{{ bucket.count }}</span>
                  <div
                    class="timeline-bar"
                    :style="{ height: timelineBarHeight(bucket.count) + '%' }"
                  ></div>
                  <div class="timeline-label">{{ formatHour(bucket.hour_bucket) }}</div>
                </div>
              </div>
            </div>

            <!-- Filter Tabs + Table -->
            <div class="table-section">
              <div class="table-header">
                <div class="filter-tabs">
                  <button
                    v-for="tab in filterTabs"
                    :key="tab.value"
                    :class="['filter-tab', { active: activeFilter === tab.value }]"
                    @click="setFilter(tab.value)"
                  >
                    {{ tab.label }}
                    <span class="tab-count">{{ tab.count }}</span>
                  </button>
                </div>

                <div class="table-actions">
                  <button
                    v-if="campaign.sent_count > 0"
                    class="action-btn resend-btn"
                    :disabled="resending"
                    @click="resendUndelivered"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                    </svg>
                    {{ resending ? 'Starting…' : 'Re-send to undelivered (' + campaign.sent_count + ')' }}
                  </button>

                  <button
                    class="action-btn export-btn"
                    :disabled="exportingCSV"
                    @click="exportCSV"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                    </svg>
                    {{ exportingCSV ? 'Exporting…' : 'Export CSV' }}
                  </button>
                </div>
              </div>

              <!-- Table -->
              <div class="table-wrapper">
                <div v-if="loading" class="table-loading">
                  <div class="loading-spinner small"></div>
                </div>

                <table v-else class="recipients-table">
                  <thead>
                    <tr>
                      <th>Phone Number</th>
                      <th>Customer Name</th>
                      <th>Engagement</th>
                      <th>Status</th>
                      <th>Delivered At</th>
                      <th>Read At</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="messages.length === 0">
                      <td colspan="7" class="empty-row">No recipients found for this filter.</td>
                    </tr>
                    <tr
                      v-for="msg in messages"
                      :key="msg.phone_number + msg.sent_at"
                      :class="['recipient-row', msg.status]"
                    >
                      <td class="phone-cell">{{ msg.phone_number }}</td>
                      <td class="name-cell">{{ msg.customer_name || '—' }}</td>
                      <td class="engagement-cell">
                        <span
                          v-if="msg.broadcast_received_count > 0"
                          :class="['eng-badge', engagementTier(msg.broadcast_received_count, msg.broadcast_read_count).cls]"
                          :title="msg.broadcast_read_count + ' reads out of ' + msg.broadcast_received_count + ' received'"
                        >
                          {{ engagementTier(msg.broadcast_received_count, msg.broadcast_read_count).icon }}
                          {{ engagementTier(msg.broadcast_received_count, msg.broadcast_read_count).label }}
                        </span>
                        <span v-else class="eng-badge new">🆕 New</span>
                      </td>
                      <td class="status-cell">
                        <span :class="['status-badge', msg.status]">
                          <span class="status-dot"></span>
                          {{ msg.status }}
                        </span>
                      </td>
                      <td class="time-cell">{{ formatTime(msg.delivered_at) }}</td>
                      <td class="time-cell">{{ formatTime(msg.read_at) }}</td>
                      <td class="error-cell">
                        <span v-if="msg.error_message" class="error-text" :title="msg.error_message">
                          {{ msg.error_message.length > 40 ? msg.error_message.substring(0, 40) + '…' : msg.error_message }}
                        </span>
                        <span v-else class="no-error">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div v-if="pagination.total_pages > 1" class="pagination">
                <button
                  class="page-btn"
                  :disabled="pagination.page === 1"
                  @click="goToPage(pagination.page - 1)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                  </svg>
                </button>

                <span class="page-info">
                  Page {{ pagination.page }} of {{ pagination.total_pages }}
                </span>

                <button
                  class="page-btn"
                  :disabled="pagination.page === pagination.total_pages"
                  @click="goToPage(pagination.page + 1)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                  </svg>
                </button>
              </div>
            </div>
          </template>

        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { AuthService } from '../services/auth'
import { getUserTimezone, formatDate, formatDateTime, formatHourLabel } from '../utils/timezone'

export default {
  name: 'WhatsAppBroadcastRecipientsView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      loading: true,
      campaign: null,
      messages: [],
      timeline: [],
      pagination: { total: 0, page: 1, per_page: 50, total_pages: 1 },
      activeFilter: 'all',
      exportingCSV: false,
      resending: false,
      userTimezone: getUserTimezone()
    }
  },

  computed: {
    campaignId () {
      return this.$route.params.id
    },

    filterTabs () {
      if (!this.campaign) return []
      return [
        { value: 'all', label: 'All', count: this.campaign.total_recipients },
        { value: 'sent', label: 'Sent', count: this.campaign.sent_count },
        { value: 'delivered', label: 'Delivered', count: this.campaign.delivered_count },
        { value: 'read', label: 'Read', count: this.campaign.read_count },
        { value: 'failed', label: 'Failed', count: this.campaign.failed_count }
      ]
    },

    timelineMax () {
      if (!this.timeline.length) return 1
      return Math.max(...this.timeline.map(b => parseInt(b.count)))
    },
    timelinePeak () {
      if (!this.timeline.length) return null
      const peak = this.timeline.reduce((a, b) => parseInt(a.count) >= parseInt(b.count) ? a : b)
      return { label: this.formatHour(peak.hour_bucket), count: peak.count }
    }
  },

  mounted () {
    this.fetchRecipients()
  },

  methods: {
    async fetchRecipients () {
      this.loading = true
      try {
        const token = AuthService.getAuthToken()
        const params = new URLSearchParams({
          status: this.activeFilter,
          page: this.pagination.page,
          per_page: this.pagination.per_page
        })

        const response = await fetch(
          `https://api.quali-d.com/whatsapp/broadcast/campaigns/${this.campaignId}/recipients?${params}`,
          { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } }
        )

        const data = await response.json()

        if (data.success) {
          this.campaign = data.campaign
          this.messages = data.messages
          this.timeline = data.timeline || []
          this.pagination = data.pagination
        }
      } catch (err) {
        console.error('Failed to load campaign recipients:', err)
      } finally {
        this.loading = false
      }
    },

    setFilter (status) {
      this.activeFilter = status
      this.pagination.page = 1
      this.fetchRecipients()
    },

    goToPage (page) {
      this.pagination.page = page
      this.fetchRecipients()
    },

    // Fetch all pages for CSV export
    async exportCSV () {
      this.exportingCSV = true
      try {
        const token = AuthService.getAuthToken()
        const params = new URLSearchParams({
          status: this.activeFilter,
          page: 1,
          per_page: 9999
        })

        const response = await fetch(
          `https://api.quali-d.com/whatsapp/broadcast/campaigns/${this.campaignId}/recipients?${params}`,
          { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } }
        )
        const data = await response.json()
        if (!data.success) return

        const rows = [
          ['Phone Number', 'Customer Name', 'Engagement', 'Status', 'Sent At', 'Delivered At', 'Read At', 'Error']
        ]

        data.messages.forEach(m => {
          const tier = this.engagementTier(m.broadcast_received_count, m.broadcast_read_count)
          rows.push([
            m.phone_number,
            m.customer_name || '',
            tier.label,
            m.status,
            m.sent_at || '',
            m.delivered_at || '',
            m.read_at || '',
            m.error_message || ''
          ])
        })

        const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.campaign.campaign_name.replace(/\s+/g, '_')}_recipients.csv`
        link.click()
        URL.revokeObjectURL(url)
      } catch (err) {
        console.error('CSV export failed:', err)
      } finally {
        this.exportingCSV = false
      }
    },

    // Navigate back to broadcast list with a re-send intent
    // (opens create modal pre-targeting the undelivered list)
    resendUndelivered () {
      this.$router.push({
        path: '/whatsapp-broadcast',
        query: {
          resend_campaign: this.campaignId,
          template: this.campaign.template_name,
          language: this.campaign.template_language
        }
      })
    },

    engagementTier (received, reads) {
      received = parseInt(received) || 0
      reads = parseInt(reads) || 0
      if (received < 2) return { label: 'New', icon: '🆕', cls: 'new' }
      const rate = received > 0 ? (reads / received) * 100 : 0
      if (rate >= 70 && received >= 3) return { label: 'Hot', icon: '🔥', cls: 'hot' }
      if (rate >= 40) return { label: 'Active', icon: '⭐', cls: 'active' }
      return { label: 'Passive', icon: '😴', cls: 'passive' }
    },

    timelineBarHeight (count) {
      return Math.max(4, Math.round((parseInt(count) / this.timelineMax) * 100))
    },

    formatHour (bucketStr) {
      const multiDay = this.timeline.length > 1 &&
        this.timeline[0].hour_bucket.slice(0, 10) !==
        this.timeline[this.timeline.length - 1].hour_bucket.slice(0, 10)
      return formatHourLabel(bucketStr, this.userTimezone, multiDay)
    },

    pct (count) {
      if (!this.campaign || !this.campaign.total_recipients) return 0
      return Math.round((count / this.campaign.total_recipients) * 100)
    },

    formatDate (dateStr) {
      return formatDate(dateStr, this.userTimezone)
    },

    formatTime (dateStr) {
      return formatDateTime(dateStr, this.userTimezone)
    }
  }
}
</script>

<style scoped>
.broadcast-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.recipients-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Page Header */
.page-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  width: fit-content;
  transition: all 0.2s;
}
.back-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.campaign-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.campaign-title-row h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
}
.campaign-meta {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--text-secondary);
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}
.status-badge.completed   { background: rgba(37,211,102,0.12); color: #25D366; }
.status-badge.in_progress { background: rgba(59,130,246,0.12); color: #3B82F6; }
.status-badge.sent        { background: rgba(148,163,184,0.15); color: #94A3B8; }
.status-badge.delivered   { background: rgba(59,130,246,0.12); color: #3B82F6; }
.status-badge.read        { background: rgba(37,211,102,0.12); color: #25D366; }
.status-badge.failed      { background: rgba(239,68,68,0.12);  color: #EF4444; }

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

/* Summary Cards */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  border-left: 4px solid transparent;
}
.summary-card.sent      { border-left-color: #94A3B8; }
.summary-card.delivered { border-left-color: #3B82F6; }
.summary-card.read      { border-left-color: #25D366; }
.summary-card.failed    { border-left-color: #EF4444; }

.summary-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  flex-shrink: 0;
}
.summary-card.sent      .summary-icon { background: rgba(148,163,184,0.15); color: #94A3B8; }
.summary-card.delivered .summary-icon { background: rgba(59,130,246,0.12);  color: #3B82F6; }
.summary-card.read      .summary-icon { background: rgba(37,211,102,0.12);  color: #25D366; }
.summary-card.failed    .summary-icon { background: rgba(239,68,68,0.12);   color: #EF4444; }

.summary-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}
.summary-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-top: 4px;
}
.summary-pct {
  font-size: 12px;
  color: var(--text-tertiary);
  margin-top: 2px;
}

/* Read Rate Timeline */
.timeline-section {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px 24px;
}

.timeline-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.timeline-title-group h3 {
  margin: 0 0 6px 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
}

.timeline-subtitle {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
  max-width: 480px;
}

.timeline-peak-badge {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  background: rgba(37, 211, 102, 0.08);
  border: 1px solid rgba(37, 211, 102, 0.25);
  border-radius: 10px;
  padding: 10px 16px;
  flex-shrink: 0;
}

.peak-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #25D366;
}

.peak-value {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.peak-count {
  font-size: 12px;
  color: var(--text-secondary);
}

.timeline-chart {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 140px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.timeline-bar-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 40px;
  flex: 1;
  max-width: 64px;
  height: 100%;
  justify-content: flex-end;
  cursor: default;
  position: relative;
}

.bar-count {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  transition: color 0.2s;
}

.timeline-bar-wrap.is-peak .bar-count {
  color: #25D366;
}

.timeline-bar {
  width: 100%;
  background: rgba(37, 211, 102, 0.35);
  border-radius: 4px 4px 0 0;
  min-height: 4px;
  transition: background 0.2s;
  border: 1px solid rgba(37, 211, 102, 0.4);
  border-bottom: none;
}

.timeline-bar-wrap.is-peak .timeline-bar {
  background: #25D366;
  border-color: #20c05a;
}

.timeline-bar-wrap:hover .timeline-bar {
  background: rgba(37, 211, 102, 0.6);
}

.timeline-bar-wrap.is-peak:hover .timeline-bar {
  background: #20c05a;
}

.timeline-label {
  font-size: 10px;
  color: var(--text-tertiary);
  white-space: nowrap;
}

/* Table Section */
.table-section {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-primary);
  gap: 16px;
  flex-wrap: wrap;
}

.filter-tabs {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.filter-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: transparent;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}
.filter-tab:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}
.filter-tab.active {
  background: rgba(37, 211, 102, 0.1);
  border-color: #25D366;
  color: #25D366;
}

.tab-count {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 11px;
}
.filter-tab.active .tab-count {
  background: rgba(37, 211, 102, 0.15);
  color: #25D366;
}

.table-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border-primary);
  transition: all 0.2s;
}
.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.export-btn {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}
.export-btn:hover:not(:disabled) {
  background: var(--bg-primary);
  color: var(--text-primary);
}

.resend-btn {
  background: rgba(59, 130, 246, 0.1);
  color: #3B82F6;
  border-color: rgba(59, 130, 246, 0.3);
}
.resend-btn:hover:not(:disabled) {
  background: rgba(59, 130, 246, 0.2);
}

/* Table */
.table-wrapper {
  overflow-x: auto;
  position: relative;
  min-height: 120px;
}

.table-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.recipients-table {
  width: 100%;
  border-collapse: collapse;
}

.recipients-table thead th {
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid var(--border-primary);
  white-space: nowrap;
  background: var(--bg-tertiary);
}

.recipients-table tbody tr {
  border-bottom: 1px solid var(--border-primary);
  transition: background 0.15s;
}
.recipients-table tbody tr:last-child {
  border-bottom: none;
}
.recipients-table tbody tr:hover {
  background: var(--bg-tertiary);
}

.recipients-table td {
  padding: 12px 16px;
  font-size: 14px;
  color: var(--text-primary);
  vertical-align: middle;
}

.phone-cell {
  font-family: monospace;
  font-size: 13px;
}
.name-cell {
  color: var(--text-secondary);
}
.time-cell {
  font-size: 13px;
  color: var(--text-secondary);
  white-space: nowrap;
}
.error-cell {
  max-width: 220px;
}
.error-text {
  font-size: 12px;
  color: #EF4444;
  cursor: help;
}
.no-error {
  color: var(--text-tertiary);
}

.empty-row {
  text-align: center;
  color: var(--text-secondary);
  padding: 48px 16px !important;
}

/* Engagement Badges */
.eng-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.eng-badge.hot     { background: rgba(251,146,60,0.15); color: #FB923C; }
.eng-badge.active  { background: rgba(250,204,21,0.15); color: #CA8A04; }
.eng-badge.passive { background: rgba(148,163,184,0.15); color: #64748B; }
.eng-badge.new     { background: rgba(99,102,241,0.12); color: #6366F1; }

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  border-top: 1px solid var(--border-primary);
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}
.page-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}
.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: var(--text-secondary);
}

/* Loading */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 0;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--border-primary);
  border-top-color: #25D366;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.loading-spinner.small {
  width: 24px;
  height: 24px;
  border-width: 2px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
